import { useContext } from 'react';
import { data_from_server_post, get_base_url } from '@/shared/http/client';
import { GlobalContext } from '@/app/providers/globalState';
import { sparkToast } from '@spark';

export const usePlaygroundApi = () => {
    const {
        getCurrentUsecase,
    } = useContext(GlobalContext);


    const inference = async (values) => {
        const currentUsecase = getCurrentUsecase();
        let url = '';

        const modelIsCommercial = values.isCommercialModel;
        const provider = values.provider;

        if (modelIsCommercial) {
            url = `${get_base_url(origin)}/inference-commercial`;
            values = {
                inference: { ...values, teacher_model: values.model_id },
                client_id: currentUsecase?.client_id,
                llm_usecase_id: currentUsecase?._id,
                provider: provider || "openai"
            };
        } else {
            if (values.examples.length === 0) {
                // Pre-dispatch guardrail. We surface the notification here
                // (no request was made) and throw so callers transition to
                // their error path instead of silently treating ``undefined``
                // as a successful empty-result response. See the matching
                // ``alreadyNotified`` flag handling in the callers.
                sparkToast.error('Please provide at least one example for optune models');
                throw Object.assign(
                    new Error('Please provide at least one example for optune models'),
                    { alreadyNotified: true }
                );
            }
            url = `${get_base_url(origin)}/optune-predictions`;
            values = {
                examples: values.examples,
                distillation_event_id: values.model_id,
                client_id: currentUsecase?.client_id,
                llm_usecase_id: currentUsecase?._id,
                // Forward the prompt so the backend can use it for summarization
                // SpecificAI models (vLLM). Ignored by classification/NER paths.
                prompt: values.prompt,
            };
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(values),
        });

        if (!response.ok) {
            // Previously we surfaced a notification and ``return``ed undefined.
            // Callers used ``.then(results => results?.error ? ... : ok)`` and
            // therefore treated the undefined as an empty success — clearing
            // ``loading`` while leaving ``conversation`` empty, which renders
            // as "No results yet. Click Generate." in the table even though
            // the request actually failed. Throwing routes failures into the
            // caller's ``.catch`` so they can reset state without overwriting
            // any prior results.
            let detail = `Request failed: ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData?.detail) detail = errorData.detail;
            } catch (e) {
                // Body wasn't JSON; fall back to the status-based message.
            }
            sparkToast.error(detail);
            throw Object.assign(new Error(detail), {
                status: response.status,
                alreadyNotified: true,
            });
        }

        return response.json();
    };

    // Default Playground examples come from the **benchmark** when one exists,
    // because the benchmark is the curated test set the team uses everywhere
    // else (Evaluation overview, Reference Response card, training metrics).
    // We only fall back to the regular dataset when the benchmark is empty so
    // brand-new use cases without a benchmark still get something to show.
    //
    // We also propagate ``response`` (the ground truth) on each item so the
    // overview table can render the per-example NER bar chart and the detail
    // view can render the Reference Response card. Earlier versions threw
    // ``response`` away here, which forced every downstream consumer to
    // re-fetch the same record by id.
    const mapRecord = (v, isBenchmark) => ({
        value: v.request_example,
        id: v._id,
        response: v.response ?? null,
        is_benchmark: isBenchmark,
    });

    const fetchExamples = async (params, successCallback) => {
        await data_from_server_post(
            "dataset-records",
            { ...params, is_benchmark: true },
            (benchmarkResponse) => {
                const benchmarkRecords = benchmarkResponse.records || [];
                if (benchmarkRecords.length > 0) {
                    successCallback(benchmarkRecords.map((v) => mapRecord(v, true)));
                    return;
                }
                data_from_server_post(
                    "dataset-records",
                    { ...params, is_benchmark: false },
                    (datasetResponse) => {
                        const datasetRecords = datasetResponse.records || [];
                        successCallback(
                            datasetRecords.map((v) => mapRecord(v, false))
                        );
                    }
                );
            }
        );
    };

    // Computes per-sample entity status (success / partial / error / missed)
    // for NER predictions against the dataset's ground truth, using the same
    // ``batch_get_teacher_entity_statuses`` the Evaluation page calls. The
    // backend returns one status array per example, in the same shape that
    // ``calculateSampleMetrics`` (shared with EntityExtractionsTable) consumes.
    const fetchEntityStatuses = async (payload, successCallback, errorCallback) => {
        await data_from_server_post(
            "playground-entity-statuses",
            payload,
            successCallback,
            errorCallback,
        );
    };

    const fetchRandomExample = async (params, successCallback) => {
        const fetchRandom = (totalCount, isBenchmark) => {
            const randomIndex = Math.floor(Math.random() * totalCount);
            data_from_server_post(
                "dataset-records",
                {
                    ...params,
                    is_benchmark: isBenchmark,
                    first_index: randomIndex,
                    num_records: 1
                },
                (response) => {
                    const records = response.records || [];
                    if (records.length > 0) {
                        successCallback({
                            value: records[0].request_example,
                            id: records[0]._id
                        });
                    } else {
                        successCallback(null);
                    }
                }
            );
        };

        // Check non-benchmark first
        await data_from_server_post(
            "dataset-records",
            { ...params, is_benchmark: false, first_index: 0, num_records: 1 },
            (response) => {
                const count = response.filtered_count || 0;
                if (count > 0) {
                    fetchRandom(count, false);
                } else {
                    // Check benchmark
                    data_from_server_post(
                        "dataset-records",
                        { ...params, is_benchmark: true, first_index: 0, num_records: 1 },
                        (bmResponse) => {
                            const bmCount = bmResponse.filtered_count || 0;
                            if (bmCount > 0) {
                                fetchRandom(bmCount, true);
                            } else {
                                successCallback(null);
                            }
                        }
                    );
                }
            }
        );
    };

    const rawResponses = async (params, successCallback, errorCallback) => {
        const currentUsecase = getCurrentUsecase();

        await data_from_server_post(
            "raw-responses",
            {
                ...params,
                teacher_model: currentUsecase.teacher_model,
                prompt: currentUsecase.prompt,
                system_prompt: currentUsecase.teacher_system_prompt || null,
                teacher_schema: currentUsecase.teacher_schema || null,
                provider: currentUsecase.teacher_provider
            },
            successCallback,
            errorCallback
        );
    };

    const saveTeacherAndPrompt = async (params, successCallback) => {
        return data_from_server_post("save_teacher_and_prompt", params, successCallback);
    };

    // Re-parse already-generated raw outputs with the usecase's current
    // parser function (after it is edited in the Inspect modal) — no new
    // LLM calls. ``params``: client_id, llm_usecase_id, raw_results.
    const parseRaw = async (params, successCallback, errorCallback) => {
        return data_from_server_post(
            "playground/parse-raw",
            params,
            successCallback,
            errorCallback
        );
    };

    const getCustomModelNames = async () => {
        const response = await fetch(`${get_base_url(origin)}/model_names`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75" }),
        });

        if (!response.ok) {
            throw new Error(`Error getting model names: ${response.status}`);
        }

        const data = await response.json();
        return data.model_names;
    };

    const getUsecaseModels = async (clientId, usecaseId) => {
        return new Promise((resolve) => {
            try {
                const currentUsecase = getCurrentUsecase();
                const client_id = clientId || currentUsecase?.client_id;
                const llm_usecase_id = usecaseId || currentUsecase?._id;

                if (!client_id || !llm_usecase_id) {
                    console.warn("Missing client_id or llm_usecase_id for getUsecaseModels");
                    resolve([]);
                    return;
                }

                data_from_server_post(
                    "usecase-models",
                    {
                        client_id,
                        llm_usecase_id
                    },
                    (response) => {
                        resolve(response?.models || []);
                    }
                );
            } catch (error) {
                console.error("Failed to fetch usecase models:", error);
                resolve([]);
            }
        });
    };

    return {
        inference,
        fetchExamples,
        fetchEntityStatuses,
        fetchRandomExample,
        rawResponses,
        saveTeacherAndPrompt,
        parseRaw,
        getCustomModelNames,
        getUsecaseModels,
    };
};